import { Alert } from "@chakra-ui/react";
import { InfoDrawerValue } from "../providers/InfoDrawer";
import Typhography from "../shared/Typhography/Typhography";
import { DOMAIN_GUIDES } from "./domainGuides";

const sectionTitle: React.CSSProperties = {
  fontFamily: "'Satoshi', sans-serif",
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--c-text-3)',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
  marginTop: 8,
};

const card: React.CSSProperties = {
  background: 'var(--c-app-bg)',
  borderRadius: 12,
  padding: '14px 16px',
};

const dot: (color: string) => React.CSSProperties = (color) => ({
  display: 'inline-block',
  width: 10,
  height: 10,
  borderRadius: '50%',
  background: color,
  marginRight: 8,
  verticalAlign: 'middle',
});

const scaleRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 0',
};

export const INFO_DRAWER_VALUES = {
  teamInvites: {
    title: "Come funzionano i link di invito",
    placement: "end",
    size: "md",
    description: (
      <div className="d-flex flex-column gap-3">
        <Typhography size="regular-16">
          Generi un link e lo mandi agli atleti come vuoi — WhatsApp, email, un
          QR appeso in spogliatoio. Chi lo apre entra da solo in questa squadra:
          non devi aggiungerli uno per uno.
        </Typhography>

        <div style={card}>
          <Typhography size="bold-14">Cosa vede l'atleta</Typhography>
          <Typhography size="regular-16">
            Il nome della squadra, i posti rimasti e la scadenza. Se ha già un
            account LeoPart ed è connesso, entra con un clic; altrimenti sceglie
            se accedere o registrarsi al momento.
          </Typhography>
        </div>

        <div style={sectionTitle}>Posti e scadenza</div>
        <Typhography size="regular-16">
          Sono i due limiti del link. Un atleta consuma un posto solo la prima
          volta: se riapre il link non ne consuma un altro. Quando i posti
          finiscono o la data passa, il link smette di funzionare da solo — non
          devi ricordarti di chiuderlo.
        </Typhography>

        <div style={sectionTitle}>Disattiva</div>
        <Typhography size="regular-16">
          Ferma subito un link, anche se ha ancora posti. Serve quando il link è
          finito dove non doveva. Puoi riattivarlo quando vuoi: chi è già
          entrato resta in squadra.
        </Typhography>

        <div style={sectionTitle}>Chi è entrato</div>
        <Typhography size="regular-16">
          Accanto a ogni link vedi quanti hanno usato quel link e quanti posti
          restano, così sai se è stato girato più del previsto.
        </Typhography>

        <Alert.Root status="warning">
          <Alert.Indicator />
          <Alert.Title>
            Chi ha il link può entrare in squadra: trattalo come una password.
            Se ti sfugge di mano, disattivalo e creane un altro.
          </Alert.Title>
        </Alert.Root>

        <Alert.Root status="info">
          <Alert.Indicator />
          <Alert.Title>
            Gli atleti che entrano occupano un posto del tuo piano, esattamente
            come quelli aggiunti a mano.
          </Alert.Title>
        </Alert.Root>
      </div>
    ),
  },

  sleep: {
    title: "Monitoraggio dello Stress",
    placement: "end",
    size: "md",
    description: (
      <div className="d-flex flex-column gap-2">
        <Typhography size="regular-16">
          Questo grafico mostra l'andamento del tuo stress psico-fisico.
        </Typhography>
        <Typhography size="regular-16" className="mb-4">
          Un trend in diminuzione indica un recupero efficace, mentre picchi
          prolungati possono segnalare la necessità di riposo.
        </Typhography>
        <Typhography size="regular-16" className="mb-4">
          Il dato è calcolato sulla base di un questionario soggettivo compilato
          quotidianamente dagli atleti.
        </Typhography>
        <Alert.Root status="info" title="This is the alert title">
          <Alert.Indicator />
          <Alert.Title>
            Il valore viene misurato in un range da 1 a 5.
          </Alert.Title>
        </Alert.Root>
      </div>
    ),
  },
  afi: {
    title: "Antifragility Index (AFI)",
    placement: "end",
    size: "lg",
    description: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontFamily: "'Satoshi', sans-serif" }}>

        {/* ── Intro ── */}
        <div>
          <Typhography size="regular-14" style={{ color: 'var(--c-text-1)', lineHeight: 1.7 }}>
            L'AFI misura la <strong style={{ color: 'var(--c-text-0)' }}>robustezza strutturale</strong> di
            un atleta nel tempo. Non risponde a <em>"è pronto oggi?"</em> — questo
            lo fa il Recovery Index. L'AFI risponde a una domanda diversa:
          </Typhography>
          <div style={{
            background: '#063444', color: '#fff', borderRadius: 12, padding: '16px 20px',
            marginTop: 12, fontFamily: "'Satoshi', sans-serif", fontSize: 15, fontWeight: 500, lineHeight: 1.5,
          }}>
            "Quanto è preparato questo atleta ad affrontare qualcosa di imprevisto?"
          </div>
          <Typhography size="regular-12" style={{ color: 'var(--c-text-3)', marginTop: 8 }}>
            Score 0–100 · Calcolato ogni settimana · Basato su Hill &amp; Kiefer (2020), Plews et al. (2025)
          </Typhography>
        </div>

        {/* ── Le 3 componenti ── */}
        <div>
          <div style={sectionTitle}>Le tre componenti</div>

          <div style={{ ...card, marginTop: 10, borderLeft: '3px solid #3b82f6' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <Typhography size="bold-14" style={{ color: 'var(--c-text-0)' }}>
                A — Stabilità Fisiologica
              </Typhography>
              <span style={{ fontSize: 11, color: 'var(--c-text-3)', fontWeight: 600 }}>Peso: 40%</span>
            </div>
            <Typhography size="regular-12" style={{ color: 'var(--c-text-1)', lineHeight: 1.6 }}>
              Misura la <strong>consistenza</strong> del sistema nervoso autonomo, non
              il suo valore assoluto. Un atleta con HRV stabile giorno dopo giorno ha
              un sistema che si auto-regola con precisione — caratteristica degli atleti
              d'élite.
            </Typhography>
            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--c-text-3)' }}>
              Metriche: RMSSD-CV · Hooper-CV · Trend RMSSD (28 giorni rolling)
            </div>
          </div>

          <div style={{ ...card, marginTop: 8, borderLeft: '3px solid #f59e0b' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <Typhography size="bold-14" style={{ color: 'var(--c-text-0)' }}>
                B — Capacità di Recovery
              </Typhography>
              <span style={{ fontSize: 11, color: 'var(--c-text-3)', fontWeight: 600 }}>Peso: 40%</span>
            </div>
            <Typhography size="regular-12" style={{ color: 'var(--c-text-1)', lineHeight: 1.6 }}>
              Misura come l'atleta ha <strong>risposto storicamente</strong> agli
              infortuni. Il Recovery Ratio confronta i giorni di recupero effettivi con
              quelli attesi dallo staff. Un atleta che recupera più velocemente del
              previsto è antifragile.
            </Typhography>
            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--c-text-3)' }}>
              Metriche: Recovery Ratio · Hooper pre-evento · N. eventi documentati
            </div>
          </div>

          <div style={{ ...card, marginTop: 8, borderLeft: '3px solid #8b5cf6' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <Typhography size="bold-14" style={{ color: 'var(--c-text-0)' }}>
                C — Margine Strutturale
              </Typhography>
              <span style={{ fontSize: 11, color: 'var(--c-text-3)', fontWeight: 600 }}>Peso: 20%</span>
            </div>
            <Typhography size="regular-12" style={{ color: 'var(--c-text-1)', lineHeight: 1.6 }}>
              Misura quanto spesso l'atleta opera con <strong>riserva sufficiente</strong>.
              Chi lavora sempre al limite è più vulnerabile quando arriva l'imprevisto.
              Un buon margine si costruisce con carico controllato, sonno adeguato e
              varietà di stimoli.
            </Typhography>
            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--c-text-3)' }}>
              Metriche: ACWR zona verde · Qualità sonno · Diversità stimoli (90 giorni)
            </div>
          </div>
        </div>

        {/* ── Scala di lettura ── */}
        <div>
          <div style={sectionTitle}>Scala di lettura</div>
          <div style={{ ...card, marginTop: 10, padding: '10px 16px' }}>
            <div style={scaleRow}>
              <span style={dot('#4dd292')} />
              <Typhography size="medium-14" style={{ color: 'var(--c-text-0)' }}>75–100</Typhography>
              <Typhography size="regular-12" style={{ color: 'var(--c-text-1)' }}>Alta robustezza</Typhography>
            </div>
            <div style={{ ...scaleRow, borderTop: '1px solid #e7e7e7' }}>
              <span style={dot('#8bc34a')} />
              <Typhography size="medium-14" style={{ color: 'var(--c-text-0)' }}>55–74</Typhography>
              <Typhography size="regular-12" style={{ color: 'var(--c-text-1)' }}>In costruzione</Typhography>
            </div>
            <div style={{ ...scaleRow, borderTop: '1px solid #e7e7e7' }}>
              <span style={dot('#f5b350')} />
              <Typhography size="medium-14" style={{ color: 'var(--c-text-0)' }}>35–54</Typhography>
              <Typhography size="regular-12" style={{ color: 'var(--c-text-1)' }}>Fragilità moderata</Typhography>
            </div>
            <div style={{ ...scaleRow, borderTop: '1px solid #e7e7e7' }}>
              <span style={dot('#f05555')} />
              <Typhography size="medium-14" style={{ color: 'var(--c-text-0)' }}>0–34</Typhography>
              <Typhography size="regular-12" style={{ color: 'var(--c-text-1)' }}>Fragilità alta</Typhography>
            </div>
          </div>
        </div>

        {/* ── Come usarlo ── */}
        <div>
          <div style={sectionTitle}>Come usarlo</div>
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Typhography size="regular-12" style={{ color: 'var(--c-text-1)', lineHeight: 1.6 }}>
              <strong style={{ color: 'var(--c-text-0)' }}>Non guardare solo lo score.</strong> Il
              pattern delle tre componenti è più informativo del numero singolo. Un atleta
              con A alta e C bassa ha fisiologia stabile ma opera al limite — il rischio
              non è oggi, è quando arriva lo shock.
            </Typhography>
            <Typhography size="regular-12" style={{ color: 'var(--c-text-1)', lineHeight: 1.6 }}>
              <strong style={{ color: 'var(--c-text-0)' }}>Il trend conta più del valore.</strong> Un
              AFI di 45 in salita è un segnale migliore di un AFI di 60 in discesa. Il
              sistema monitora le variazioni settimanali e genera alert automatici quando
              il calo supera i 12 punti in due settimane.
            </Typhography>
          </div>
        </div>

        {/* ── Nota importante ── */}
        <div style={{
          background: '#eff6ff', borderRadius: 12, padding: '14px 16px',
          border: '1px solid #dbeafe',
        }}>
          <Typhography size="bold-12" style={{ color: '#1e40af', marginBottom: 4 }}>
            Nota importante
          </Typhography>
          <Typhography size="regular-12" style={{ color: '#1e40af', lineHeight: 1.6 }}>
            L'AFI non è un sistema di predizione infortuni traumatici. Non predice
            il cigno nero — misura quanto l'atleta è preparato a rispondere quando
            arriva. Per gli eventi imprevedibili, l'unica risposta corretta è
            costruire robustezza nel tempo.
          </Typhography>
        </div>
      </div>
    ),
  },
  // Guide di dominio (feature #16): ACWR, sRPE, RI, monotonia, Δ%, copertura.
  // Definite a parte in domainGuides.tsx (SCSS module + soglie da @leopart/calc).
  ...DOMAIN_GUIDES,
  // `satisfies` (non annotazione `: Record`): preserva le chiavi letterali, così
  // `keyof IInfoDrawerValues` è l'unione reale e InfoButton.infoKey è type-safe.
} as const satisfies Record<string, InfoDrawerValue>;

export type IInfoDrawerValues = typeof INFO_DRAWER_VALUES;
